import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sliders, Check, Edit, Plus, X } from "lucide-react";
import {
  PACKAGE_DELIVERABLES,
  getBasePackageInfo,
  getBasePackageDeliverables,
  groupDeliverablesByCategory
} from "@/lib/package-config";
import { CATEGORY_CONFIG } from "@/lib/category-config";
import { cn } from "@/lib/utils";

interface CustomPackageCardProps {
  selectedDeliverables: string[];
  selectedShowCount: number;
  basePackageTier: 'set-core' | 'set-audio' | 'set-video';
  onEdit: () => void;
  onAddToProposal: () => void;
  onClear: () => void;
  disabled?: boolean;
}

export function CustomPackageCard({
  selectedDeliverables,
  selectedShowCount,
  basePackageTier,
  onEdit,
  onAddToProposal,
  onClear,
  disabled = false,
}: CustomPackageCardProps) {
  // Get base package deliverables based on selected tier
  const basePackageDeliverables = getBasePackageDeliverables(basePackageTier);
  const basePackageNames = basePackageDeliverables.map(d => d.name);

  // Get base package display info
  const basePackageInfo = getBasePackageInfo(basePackageTier);

  // Get all deliverable items for categorization
  const allDeliverableItems = PACKAGE_DELIVERABLES.filter(item =>
    selectedDeliverables.includes(item.name)
  );

  // Group selected add-ons by category
  const selectedByCategory = groupDeliverablesByCategory(allDeliverableItems);

  // Determine which categories have selections
  const categoriesWithSelections = {
    mobile: selectedByCategory.mobile.length > 0,
    venue: selectedByCategory.venue.length > 0,
    media: selectedByCategory.media.length > 0,
  };

  const categoryCount = Object.values(categoriesWithSelections).filter(Boolean).length;

  // Combine base package + selected add-ons for total count
  const allDeliverables = [...basePackageNames, ...selectedDeliverables];

  return (
    <Card className="border-2 border-green-600 bg-gradient-to-r from-green-50 to-emerald-50 overflow-hidden shadow-lg hover:shadow-xl transition-all">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center">
              <Sliders className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-green-600">SET Custom</h3>
              <p className="text-sm text-gray-600">Based on {basePackageInfo.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge className="bg-green-600 text-white text-sm px-3 py-1">
              Custom Package
            </Badge>
            <div className="text-right">
              <div className="text-sm text-gray-600 font-medium">Pricing</div>
              <div className="text-base font-bold text-green-600">Contact Us</div>
            </div>
          </div>
        </div>

        {/* Deliverables Layout - Mirror Modal Structure */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex gap-6">
            {/* Left Panel: Base Package (1/3 width) */}
            <div className="w-1/3 border-r pr-4">
              <div className="mb-3">
                <h4 className="font-semibold text-sm mb-1 text-gray-700">
                  Starting with {basePackageInfo.name}
                </h4>
                <p className="text-xs text-gray-600">
                  {basePackageInfo.description}
                </p>
              </div>

              <div className="space-y-2">
                {basePackageDeliverables.map((item, idx) => {
                  // Get the appropriate note based on selected tier
                  const note = basePackageTier === 'set-core' ? item.setCore.note :
                              basePackageTier === 'set-audio' ? item.setAudio.note :
                              item.setVideo.note;

                  return (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-700 leading-tight">{item.name}</div>
                        {note && (
                          <div className="text-xs text-gray-500 mt-0.5">{note}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Panel: Selected Add-ons (2/3 width) */}
            <div className="flex-1">
              {selectedDeliverables.length > 0 ? (
                <>
                  <div className="mb-3">
                    <h4 className="font-semibold text-sm mb-1 text-green-600">
                      Selected Upgrades
                    </h4>
                    <p className="text-xs text-gray-600">
                      {selectedDeliverables.length} optional upgrade{selectedDeliverables.length !== 1 ? 's' : ''} added
                    </p>
                  </div>

                  {/* Smart column layout based on category count */}
                  <div className={cn(
                    categoryCount === 1 ? "" : "grid grid-cols-2 gap-4"
                  )}>
                    {/* Column 1 or Single Column */}
                    <div className="space-y-3">
                      {/* Mobile Experience */}
                      {categoriesWithSelections.mobile && (
                        <div>
                          <div className={cn(
                            "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                            CATEGORY_CONFIG.mobile.bgColor,
                            CATEGORY_CONFIG.mobile.borderColor
                          )}>
                            <CATEGORY_CONFIG.mobile.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.mobile.textColor)} />
                            <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.mobile.textColor)}>
                              {CATEGORY_CONFIG.mobile.title}
                            </h5>
                          </div>
                          <div className="space-y-1">
                            {selectedByCategory.mobile.map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* In Show/Venue - Only in column 1 if: single category, OR 3 categories, OR 2 categories with media */}
                      {categoriesWithSelections.venue && (categoryCount !== 2 || categoriesWithSelections.media) && (
                        <div>
                          <div className={cn(
                            "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                            CATEGORY_CONFIG.venue.bgColor,
                            CATEGORY_CONFIG.venue.borderColor
                          )}>
                            <CATEGORY_CONFIG.venue.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.venue.textColor)} />
                            <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.venue.textColor)}>
                              {CATEGORY_CONFIG.venue.title}
                            </h5>
                          </div>
                          <div className="space-y-1">
                            {selectedByCategory.venue.map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Media - Only here if it's the only category OR if it's 2 categories (mobile + media or venue + media) */}
                      {categoriesWithSelections.media && categoryCount === 1 && (
                        <div>
                          <div className={cn(
                            "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                            CATEGORY_CONFIG.media.bgColor,
                            CATEGORY_CONFIG.media.borderColor
                          )}>
                            <CATEGORY_CONFIG.media.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.media.textColor)} />
                            <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.media.textColor)}>
                              {CATEGORY_CONFIG.media.title}
                            </h5>
                          </div>
                          <div className="space-y-1">
                            {selectedByCategory.media.map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Column 2 - Only if categoryCount > 1 */}
                    {categoryCount > 1 && (
                      <div className="space-y-3">
                        {/* Venue - In column 2 only if we have exactly 2 categories (mobile + venue) */}
                        {categoriesWithSelections.venue && categoryCount === 2 && !categoriesWithSelections.media && (
                          <div>
                            <div className={cn(
                              "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                              CATEGORY_CONFIG.venue.bgColor,
                              CATEGORY_CONFIG.venue.borderColor
                            )}>
                              <CATEGORY_CONFIG.venue.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.venue.textColor)} />
                              <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.venue.textColor)}>
                                {CATEGORY_CONFIG.venue.title}
                              </h5>
                            </div>
                            <div className="space-y-1">
                              {selectedByCategory.venue.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                  <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                  <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Media - In column 2 for 2+ categories */}
                        {categoriesWithSelections.media && (
                          <div>
                            <div className={cn(
                              "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                              CATEGORY_CONFIG.media.bgColor,
                              CATEGORY_CONFIG.media.borderColor
                            )}>
                              <CATEGORY_CONFIG.media.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.media.textColor)} />
                              <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.media.textColor)}>
                                {CATEGORY_CONFIG.media.title}
                              </h5>
                            </div>
                            <div className="space-y-1">
                              {selectedByCategory.media.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                  <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                  <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No optional upgrades selected
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between border-t border-green-200 pt-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{allDeliverables.length}</span> deliverables included
            <span className="text-gray-400 mx-2">•</span>
            <span className="font-medium">{basePackageNames.length}</span> from {basePackageInfo.name} +{" "}
            <span className="font-medium">{selectedDeliverables.length}</span> upgrades
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClear}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            >
              <X className="w-4 h-4 mr-2" />
              Clear Package
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Package
            </Button>

            <Button
              size="sm"
              onClick={onAddToProposal}
              disabled={disabled || selectedShowCount === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {selectedShowCount > 0 ? `${selectedShowCount} show${selectedShowCount !== 1 ? 's' : ''}` : 'shows'} to plan
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
